import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useOutletContext } from 'react-router-dom';
import useAuthAPI from '../../api/auth';
import LoginFormPage from './Pages/LoginFormPage';
import FirstLogin, { checkIfPasswordIsExpired } from './Pages/FirstLogin';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  userDataAtom,
  openSnackbarAdviseAtom,
  authCheckedAtom,
} from '../../Atoms';
import { SESSION_EXPIRED_FLAG } from '../../Hooks/useProxy';
import { CREDITS_ALERT_SHOWN_FLAG } from '../../Utils/constants/general';

const Login = function Login() {
  const intl = useIntl();
  const AuthAPI = useAuthAPI();
  const navigate = useNavigate();
  const { onLogin } = useOutletContext() || {};
  const setUserData = useSetAtom(userDataAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const authChecked = useAtomValue(authCheckedAtom);
  const [loginError, setLoginError] = useState(null);
  const [needToChangePassword, setNeedToChangePassword] = useState(false);
  const [loginInPending, setLoginInPending] = useState(false);

  // Once the session check is done: resets the credits alert and shows a pending "session expired".
  useEffect(() => {
    if (!authChecked) return;
    sessionStorage.removeItem(CREDITS_ALERT_SHOWN_FLAG);
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      setLoginError({ payload: 'SESSION_EXPIRED' });
    }
  }, [authChecked]);

  useEffect(() => {
    if (loginError && !needToChangePassword)
      setNeedToChangePassword(checkIfPasswordIsExpired(loginError));
  }, [loginError]);

  const handleLogin = (data, captchaValue) => {
    if (data === 'passkey') {
      AuthAPI.fido2Authenticate(
        (result) => {
          setOpenSnackbarAdvise({
            message: intl.messages.SETUP_SUCCESS_2FA,
            severity: 'success',
          });
          setUserData({
            username: result.userName,
          });
          onLogin();
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.ERROR_SETUP_2FA,
            severity: 'error',
          });
        }
      );
    } else if (
      !('newPassword' in data) ||
      data.newPassword === data.confirmPassword
    ) {
      if (!captchaValue) {
        setLoginError({ payload: 'CAPTCHA_NOT_VALID' });
        return;
      }
      setLoginInPending(true);
      AuthAPI.login(
        {
          username: data.username,
          password: data.password,
          captchaValue,
          newPassword: data?.newPassword,
        },
        (result) => {
          setLoginInPending(false);
          if (result.requiresTwoFactor) {
            navigate('/verify2FA', {
              state: { username: data.username, password: data.password },
            });
          } else {
            onLogin();
          }
        },
        (error) => {
          setLoginInPending(false);
          setLoginError(error?.payload ? error : { payload: 'LOGIN_ERROR' });
        }
      );
    } else {
      setLoginError({ payload: 'PASSWORD_MISMATCH' });
    }
  };

  return (
    <LoginFormPage
      onSubmit={handleLogin}
      loginInPending={loginInPending}
      needToChangePassword={needToChangePassword}
      widget={<FirstLogin loginError={loginError} />}
    />
  );
};

export default Login;
